import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
    AcademicCapIcon,
    ArrowRightIcon,
    SparklesIcon,
    BriefcaseIcon,
    GlobeAltIcon,
    TrophyIcon
} from '@heroicons/react/24/outline'

const TopDegrees = ({ degrees = [] }) => {
    if (degrees.length === 0) {
        return null
    }

    // Limit to top 12 degrees as requested
    const displayDegrees = degrees.slice(0, 12)

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
            },
        },
    }

    const itemVariants = {
        hidden: { opacity: 0, scale: 0.9, y: 20 },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: 'easeOut',
            },
        },
    }

    // Helper to get a random background gradient for eye-catching design
    const getGradientStyle = (index) => {
        const gradients = [
            'from-blue-50 to-indigo-50 border-blue-100',
            'from-purple-50 to-pink-50 border-purple-100',
            'from-emerald-50 to-teal-50 border-emerald-100',
            'from-orange-50 to-amber-50 border-orange-100',
            'from-rose-50 to-red-50 border-rose-100',
            'from-cyan-50 to-blue-50 border-cyan-100',
        ]
        return gradients[index % gradients.length]
    }

    // Helper to get a random icon color
    const getIconColor = (index) => {
        const colors = [
            'text-blue-600 bg-blue-100',
            'text-purple-600 bg-purple-100',
            'text-emerald-600 bg-emerald-100',
            'text-orange-600 bg-orange-100',
            'text-rose-600 bg-rose-100',
            'text-cyan-600 bg-cyan-100',
        ]
        return colors[index % colors.length]
    }

    return (
        <section className="py-16 lg:py-24 bg-white relative overflow-hidden">
            {/* Background elements for eye-catching design */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-30">
                <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary-100 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-accent-100 rounded-full blur-3xl"></div>
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <div className="inline-flex items-center space-x-2 bg-primary-50 px-4 py-1.5 rounded-full mb-4 border border-primary-100">
                        <SparklesIcon className="w-5 h-5 text-primary-600" />
                        <span className="text-sm font-bold text-primary-700 uppercase tracking-wider">Top Rated Programs</span>
                    </div>
                    <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-6 leading-tight">
                        Explore Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-accent-600">Top Programs</span>
                    </h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto font-medium">
                        Accelerate your career with our most popular undergraduate and postgraduate programs, recognized globally and industry-aligned.
                    </p>
                </motion.div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
                >
                    {displayDegrees.map((degree, index) => (
                        <motion.div
                            key={degree._id}
                            variants={itemVariants}
                            whileHover={{ y: -8, transition: { duration: 0.2 } }}
                        >
                            <Link
                                to={`/courses?degrees=${degree._id}`}
                                className={`block h-full bg-gradient-to-br ${getGradientStyle(index)} border-2 p-8 rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 group relative overflow-hidden`}
                            >
                                {/* Decorative circle */}
                                <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-white/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>

                                <div className="flex flex-col h-full relative z-10">
                                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300 ${getIconColor(index)}`}>
                                        <AcademicCapIcon className="w-8 h-8" />
                                    </div>

                                    <div className="mb-4">
                                        <span className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1 block">
                                            {degree.level}
                                        </span>
                                        <h3 className="text-2xl font-black text-gray-900 leading-tight group-hover:text-primary-700 transition-colors">
                                            {degree.name}
                                        </h3>
                                    </div>

                                    {degree.description && (
                                        <p className="text-sm text-gray-600 line-clamp-2 mb-6 font-medium leading-relaxed">
                                            {degree.description}
                                        </p>
                                    )}

                                    <div className="mt-auto pt-6 border-t border-gray-200/50 flex items-center justify-between">
                                        <div className="flex items-center space-x-1.5">
                                            <TrophyIcon className="w-4 h-4 text-amber-500" />
                                            <span className="text-xs font-bold text-gray-500 uppercase tracking-tighter">Industry Standard</span>
                                        </div>
                                        <div className="bg-white w-8 h-8 rounded-full flex items-center justify-center shadow-md group-hover:bg-primary-600 group-hover:text-white transition-all duration-300 transform group-hover:translate-x-1">
                                            <ArrowRightIcon className="w-4 h-4" />
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="mt-20 text-center"
                >
                    <Link
                        to="/courses"
                        className="btn-outline btn-lg inline-flex items-center space-x-2"
                    >
                        <span>View All Available Programs</span>
                        <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                    </Link>
                </motion.div>
            </div>
        </section>
    )
}

export default TopDegrees
